import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectIncident } from 'slices/commonSelectors';
import { resolveIncidentFormFamily } from '../formFamily';
import { useFormVersionTipsForPeriod } from '../hooks/icsFormVersionHooks';
import { useIcs215ExistingTip } from '../hooks/ics215FormVersionHooks';
import { useIncidentInfo } from '../hooks/incidentInfoHooks';
import { useIncidentObjectives } from '../hooks/incidentObjectiveHooks';
import { useWorkAssignmentListAttachments } from '../hooks/assignmentListHooks';
import { useIncidentResourceRequests } from '../hooks/incidentResourceRequestHooks';
import { useICSContext } from './ICSContext';
import {
  buildStepperFormStatuses,
  type StepperFormStatusInfo,
} from './stepperFormStatus';

export function usePlanningStepperFormStatuses(): Record<
  string,
  StepperFormStatusInfo
> {
  const { workingPeriodNumber } = useICSContext();
  const incident = useSelector(selectIncident);
  const family = resolveIncidentFormFamily(incident);

  const { data: tips = [] } = useFormVersionTipsForPeriod(workingPeriodNumber);
  const { data: tip215 = null } = useIcs215ExistingTip(workingPeriodNumber);
  const { data: walas = [] } = useWorkAssignmentListAttachments();
  const { data: resourceRequests = [] } = useIncidentResourceRequests();
  const { data: incidentInfo } = useIncidentInfo();
  const { data: objectives = [] } = useIncidentObjectives();

  return useMemo(() => {
    const liveObjectives = objectives.filter(
      (objective) => !objective.archived
    );
    return buildStepperFormStatuses({
      tips,
      tip215,
      walas,
      resourceRequests,
      workingPeriodNumber,
      ics202: {
        incidentInfo: incidentInfo as Record<string, unknown> | undefined,
        hasObjectives: liveObjectives.length > 0,
        attachmentManifest: incidentInfo?.iapAttachmentManifest,
        family,
      },
    });
  }, [
    family,
    incidentInfo,
    objectives,
    tip215,
    tips,
    walas,
    resourceRequests,
    workingPeriodNumber,
  ]);
}
